import { useCallback, useState } from 'react';
import { toZwApiError, type ZwApiError } from '../api/client';
import type { BookingRequest, BookingResponse } from '../api/types';
import { useZeitwahl } from '../provider';

export type BookingSubmitStatus = 'idle' | 'submitting' | 'done' | 'error';

export interface UseBookingResult {
  result: BookingResponse | null;
  status: BookingSubmitStatus;
  error: ZwApiError | null;
  submit: (body: BookingRequest) => Promise<BookingResponse>;
  reset: () => void;
}

interface BookingState {
  result: BookingResponse | null;
  status: BookingSubmitStatus;
  error: ZwApiError | null;
}

const idle: BookingState = { result: null, status: 'idle', error: null };

export function useBooking(org: string): UseBookingResult {
  const { api } = useZeitwahl();
  const [state, setState] = useState<BookingState>(idle);

  const submit = useCallback<UseBookingResult['submit']>(
    async (body) => {
      setState({ result: null, status: 'submitting', error: null });
      try {
        const result = await api.createBooking(org, body);
        setState({ result, status: 'done', error: null });
        return result;
      } catch (error) {
        const err = toZwApiError(error);
        setState({ result: null, status: 'error', error: err });
        throw err;
      }
    },
    [api, org]
  );

  const reset = useCallback(() => setState(idle), []);
  return { ...state, submit, reset };
}
